import { ReactNode, useEffect, useRef, useState } from 'react';
import VerificationModal from '../modals/verification-modal';
import { getUrlParameters } from '../../utility/utility';
import UsersServices from '@/services/users-services';
import { ShowErrorResponseMessage } from '@/utility/api-utility';
import { setStorage } from '@/utility/storage';
import CustomBanner from '../../assets/images/custom_banner.webp';
import CustomBannerMobile from '../../assets/images/custom_banner_small.webp';
import CustomBannerTitleDesktop from '../../assets/images/banner_title_desktop.webp';
import CustomBannerTitleMobile from '../../assets/images/banner_title_mobile.webp';
import { useStrings } from '@/contexts/strings-context';

const SCREWFIX_CUSTOM_URL = 'https://www.screwfix.com/landingpage/screwfix-custom';

function Verification({ children }: { children: ReactNode }) {
  const [isVerified, setIsVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { email = false, phone = false, order: orderNo, token, input = '' } = getUrlParameters();
  const hasMissingArguments = !orderNo || !token;
  const autoVerifiedRef = useRef(false);
  const strings = useStrings();

  const handleVerification = async (email: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const { data: response } = await UsersServices.verify({ email, token, orderNo });
      const { data } = response;
      const { val: clid = '', plugin: pluginUrl = '', key = '' } = data;

      setStorage('clid', clid);
      setStorage('email', email);
      setStorage('pluginUrl', pluginUrl);
      setStorage('key', key);
      setIsVerified(true);
    } catch (error) {
      ShowErrorResponseMessage({ error });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!input || !orderNo || !token || autoVerifiedRef.current) return;
    autoVerifiedRef.current = true;

    setStorage('input', input);
    handleVerification(input);
  }, [input, orderNo, token]);

  if (hasMissingArguments) {
    return (
      <div className="flex flex-1 w-full h-full flex-col items-center justify-start mt-60 sm:mt-70 p-10 bg-slate-50">
        <div className="w-full max-w-[634px] text-center sm:max-w-[1146px] [container-type:inline-size]">
          <a className="block w-full" href={SCREWFIX_CUSTOM_URL}>
            <img className="block h-auto w-full sm:hidden" src={CustomBannerMobile} alt="Mobile banner" />
            <img className="hidden h-auto w-full sm:block" src={CustomBanner} alt="Desktop banner" />
          </a>

          <div className="mt-10 w-full max-w-full text-[24px] font-extrabold leading-[1.05] whitespace-normal [overflow-wrap:anywhere] [font-family:screwfix_betaheavy,sans-serif] sm:mt-15 sm:text-[clamp(22px,3.8cqi,42px)]">
            <img className="block h-auto w-full sm:hidden" src={CustomBannerTitleMobile} alt="Mobile banner" />
            <img className="hidden h-auto w-full sm:block" src={CustomBannerTitleDesktop} alt="Desktop banner" />
          </div>

          <div className="mt-10 sm:mt-15 bg-white [clip-path:polygon(20px_0,calc(100%-20px)_0,100%_20px,100%_calc(100%-20px),calc(100%-20px)_100%,20px_100%,0_calc(100%-20px),0_20px)] p-[21px]">
            <div className="bg-white [clip-path:polygon(20px_0,calc(100%-20px)_0,100%_20px,100%_calc(100%-20px),calc(100%-20px)_100%,20px_100%,0_calc(100%-20px),0_20px)] px-6 py-5 text-center font-[Arial] text-[20px] leading-normal sm:px-8 sm:py-6 sm:text-[24px] font-medium">
              <span className="text-slate-600">Please </span>

              <a
                className="font-bold underline underline-offset-4 [overflow-wrap:anywhere] sm:text-[26px]"
                href={SCREWFIX_CUSTOM_URL}
              >
                Click Here
              </a>

              <span className="text-slate-600"> to place your order</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isVerified) return <>{children}</>;

  return (
    <div className="min-h-0 w-full flex-1 overflow-y-auto bg-slate-50">
      <VerificationModal
        verifyByEmail={email}
        verifyByPhone={phone}
        orderNo={orderNo}
        token={token}
        confirmText="Continue"
        description={email ? strings.ENTER_EMAIL_CONTENT : strings.ENTER_PHONE_CONTENT}
        onConfirmAction={handleVerification}
        title={email ? strings.ENTER_EMAIL_TITLE : strings.ENTER_PHONE_TITLE}
        isSubmitting={isSubmitting}
        open
      />
    </div>
  );
}

export default Verification;
